/**
 * Shared TanStack Query keys of dashboard blocks (KAN-723).
 *
 * Kept apart from the API modules: a change on the client card (own contact)
 * refreshes the attention block and the funnel, and tests mock the API
 * modules without these constants.
 */

/** Prefix of the "Требуют внимания" block queries. */
export const ATTENTION_QUERY_KEY = ['dashboard-attention'] as const;
/**
 * KAN-734: prefix of the "Отработанные" view queries. Nested under
 * ATTENTION_QUERY_KEY, so every refresh of the attention block (row actions,
 * client card changes) also refreshes the worked list.
 */
export const ATTENTION_WORKED_QUERY_KEY = [...ATTENTION_QUERY_KEY, 'worked'] as const;
/** Dashboard funnel block. */
export const DASHBOARD_FUNNEL_QUERY_KEY = ['dashboard-funnel'] as const;
/**
 * KAN-737: per-stage stats of the dashboard funnel block (one query per period).
 * Nested under DASHBOARD_FUNNEL_QUERY_KEY, so every funnel refresh covers it.
 */
export const FUNNEL_STAGES_QUERY_KEY = [...DASHBOARD_FUNNEL_QUERY_KEY, 'stages'] as const;
/** Prefix of the funnel analytics page queries. */
export const FUNNEL_ANALYTICS_QUERY_KEY = ['funnel-analytics'] as const;
/** KAN-735: prefix of the expenses report queries (page and dashboard banner). */
export const EXPENSES_QUERY_KEY = ['expenses'] as const;
/** KAN-743: prefix of the catalog queries (categories, product list, product card). */
export const CATALOG_QUERY_KEY = ['catalog'] as const;
/**
 * KAN-744: prefix of the managers queries (list, card, funnel of a manager).
 * Card: [...MANAGERS_QUERY_KEY, 'detail', id]; funnel: [..., 'funnel', id, period, channel].
 */
export const MANAGERS_QUERY_KEY = ['managers'] as const;
/**
 * KAN-746: prefix of the orders queries (e-commerce): list
 * [...ORDERS_QUERY_KEY, 'list', params], counts [..., 'counts'],
 * order card [..., 'detail', id].
 */
export const ORDERS_QUERY_KEY = ['orders'] as const;
/**
 * KAN-752: prefix of the users queries: list ['users', filters] (UsersPage),
 * user card [...USERS_QUERY_KEY, 'detail', id] (UserDetailPage).
 */
export const USERS_QUERY_KEY = ['users'] as const;
/**
 * KAN-756: доска CRM. Вложена в ATTENTION_QUERY_KEY: действия блока
 * "Требуют внимания" (дашборд и CRM) и правки карточки клиента обновляют
 * и доску.
 */
export const CRM_BOARD_QUERY_KEY = [...ATTENTION_QUERY_KEY, 'crm-board'] as const;
/**
 * KAN-862: канбан-доска CRM: обзор [..., 'board', outcome, activeDays], порции
 * этапа [..., 'stage', id, outcome, activeDays]. Вложена в ATTENTION_QUERY_KEY:
 * правки таблицы CRM и карточки клиента обновляют и доску.
 */
export const CRM_PIPELINE_QUERY_KEY = [...ATTENTION_QUERY_KEY, 'crm-pipeline'] as const;
/**
 * KAN-880: таблица лидов CRM (GET /api/v1/crm/leads/) страницами: [..., params].
 * Вложена в ATTENTION_QUERY_KEY: действия и правки строк обновляют и её.
 */
export const CRM_LEADS_QUERY_KEY = [...ATTENTION_QUERY_KEY, 'crm-leads'] as const;
/**
 * KAN-956: "Встречи и перезвоны на сегодня" (GET /api/v1/dashboard/today-steps/).
 * Вложена в ATTENTION_QUERY_KEY: действия строк внимания и правки клиента обновляют и её.
 */
export const TODAY_STEPS_QUERY_KEY = [...ATTENTION_QUERY_KEY, 'today-steps'] as const;
/** KAN-964: число шагов по дням месяца (календарь раздела); вложен в TODAY_STEPS_QUERY_KEY. */
export const TODAY_STEPS_MONTH_QUERY_KEY = [...TODAY_STEPS_QUERY_KEY, 'month'] as const;
/**
 * KAN-755: скидки и группы товаров: типы [..., 'types'], список
 * [..., 'list', params], карточка [..., 'detail', id], группы [..., 'groups'],
 * карточка группы [..., 'group', id].
 */
export const DISCOUNTS_QUERY_KEY = ['discounts'] as const;
/** KAN-783: блок "Продажи по менеджерам" дашборда: [..., date_from, date_to]. */
export const SALES_BY_MANAGER_QUERY_KEY = ['dashboard-sales-by-manager'] as const;
/** KAN-810: страница "Лиды по теплоте": [..., параметры списка]. */
export const LEAD_TEMPERATURE_QUERY_KEY = ['lead-temperature'] as const;
/** KAN-976: кошелёк кабинета: [..., "entries", фильтры] для журнала. */
export const WALLET_QUERY_KEY = ['wallet'] as const;
